import { useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect, useSyncExternalStore } from 'react';
import { useMe } from '@/lib/me';
import { getRealtime, type RealtimeClient, type RealtimeStatus } from '@/lib/realtime';
import { useScope } from '@/lib/scope';

const RealtimeContext = createContext<RealtimeClient | null>(null);

/** One WebSocket for the tab, watching the projects in scope. */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const rt = getRealtime(qc);
  const me = useMe();
  const scope = useScope();
  const ids = (scope.projectIds.length ? scope.projectIds : me.projects.map((p) => p.id)).join(',');

  useEffect(() => {
    rt.start();
    return () => rt.stop();
  }, [rt]);

  useEffect(() => {
    rt.watch(ids ? ids.split(',') : []);
  }, [rt, ids]);

  return <RealtimeContext.Provider value={rt}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): RealtimeClient | null {
  return useContext(RealtimeContext);
}

const idle = (): RealtimeStatus => 'idle';
const noSubscribe = () => () => undefined;

/** Connection state of the tab's WebSocket ('idle' outside the shell). */
export function useRealtimeState(): RealtimeStatus {
  const rt = useRealtime();
  return useSyncExternalStore(rt?.subscribeStatus ?? noSubscribe, rt?.getStatus ?? idle, rt?.getStatus ?? idle);
}
